import { Card as HeroUiCard, CardBody, CardHeader, Divider, extendVariants } from '@heroui/react';
import { useCallback, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import './locales/Card/card-en.json';
import { DismissButton } from './DismissButton';

type Props = {
    children: React.ReactNode;
    className?: string;
    header?: React.ReactNode;
    dividers?: { afterHeader?: boolean };
    width?: 342 | 500 | 'full';
    padding?: 0 | 2.5 | 'default' | 'compact-header' | 'top-6';
    allowOverflow?: boolean;
    radius?: 8 | 5 | 2;
    shadow?: boolean;
    startBorderColor?: 'primary' | 'purple' | 'yellow';
    dismissible?: boolean;
    onDismiss?: () => void;
    wrapperBorder?: 'default' | 'none' | 'allButStart';
};

const ExtendedCard = extendVariants(HeroUiCard, {
    variants: {
        shadow: {
            card: {
                base: 'shadow-[0_2px_10px_0_rgba(0,0,0,0.1)]',
            },
            none: {
                base: 'shadow-none',
            },
        },
        // See https://github.com/heroui-inc/heroui/issues/5293 and comment in ImportantCtaBox.tsx
        // for why our radius variants are not yet being applied correctly.
        radius: {
            8: {
                base: 'rounded-8',
                header: 'rounded-t-8',
                footer: 'rounded-b-8',
            },
            5: {
                base: 'rounded-5',
                header: 'rounded-t-5',
                footer: 'rounded-b-5',
            },
            2: {
                base: 'rounded-2',
                header: 'rounded-t-2',
                footer: 'rounded-b-2',
            },
        },
    },
    defaultVariants: {
        radius: 8,
        shadow: 'card',
    },
});

function getWidthClass(width: Props['width'], dismissible: Props['dismissible']) {
    const maxWidth = dismissible ? 'max-w-[calc(100%-12px)]' : 'max-w-full';

    if (width === 342) return `w-[342px] ${maxWidth}`;
    if (width === 500) return `w-[500px] ${maxWidth}`;
    if (width === 'full') return `w-full ${maxWidth}`;
    return '';
}

type PaddingMap = {
    t: string;
    r: string;
    b: string;
    l: string;
};

// Use !important prefix to override HeroUI CardHeader/CardBody's default `p-3`,
// which twMerge doesn't deduplicate against custom spacing values like `pt-4.5`.
const PADDING: Record<NonNullable<Props['padding']>, PaddingMap> = {
    2.5: { t: '!pt-2.5', r: '!pe-2.5', b: '!pb-2.5', l: '!ps-2.5' },
    default: { t: '!pt-4.5', r: '!pe-5', b: '!pb-5', l: '!ps-5' },
    'compact-header': { t: '!pt-3', r: '!pe-5', b: '!pb-5', l: '!ps-5' },
    0: { t: '!pt-0', r: '!pe-0', b: '!pb-0', l: '!ps-0' },
    'top-6': { t: '!pt-6', r: '!pe-5', b: '!pb-5', l: '!ps-5' },
};

function getBodyPadding({ dividers, header, padding }: Pick<Props, 'dividers' | 'padding' | 'header'>): string {
    padding = padding ?? 'default';

    // When there is a header but no divider, the top padding is on the header, so we
    // don't want to apply the top padding to the body.
    // We can't put padding on the base because we want dividers to stretch to the borders
    const topPadding = header && !dividers?.afterHeader ? 'pt-0' : PADDING[padding].t;

    return `${topPadding} ${PADDING[padding].r} ${PADDING[padding].b} ${PADDING[padding].l}`;
}

function getHeaderPadding({ padding }: { padding: Props['padding'] }): string {
    padding = padding ?? 'default';

    // See comment in getBodyPadding about the padding on the header
    const bottomPadding = padding === 'compact-header' ? '!pb-3' : '!pb-2.5';
    return `${PADDING[padding].t} ${PADDING[padding].r} ${bottomPadding} ${PADDING[padding].l}`;
}

function getStartBorder(startBorderColor: Props['startBorderColor']) {
    if (!startBorderColor) return '';

    const startBorderColorClassname = {
        primary: 'border-s-primary',
        purple: 'border-s-purple',
        yellow: 'border-s-yellow',
    }[startBorderColor];

    return `border-s-4 ${startBorderColorClassname}`;
}

function getOverflowVisibilityClass(allowOverflow: Props['allowOverflow'], dismissible: Props['dismissible']) {
    return allowOverflow || dismissible ? 'overflow-visible' : 'overflow-hidden';
}

function getWrapperBorderClass(wrapperBorder: Props['wrapperBorder']) {
    if (wrapperBorder === 'none') return 'border-none';
    if (wrapperBorder === 'allButStart') return 'border border-solid border-slate-grey-200 border-s-0';
    return 'border border-solid border-slate-grey-200';
}

export function Card({
    children,
    className,
    header,
    dividers = { afterHeader: false },
    width,
    padding = 'default',
    allowOverflow = false,
    shadow = true,
    startBorderColor,
    radius = 8,
    dismissible = false,
    onDismiss,
    wrapperBorder = 'default',
}: Props) {
    const [isDismissed, setIsDismissed] = useState(false);

    const handleDismiss = useCallback(() => {
        setIsDismissed(true);
        onDismiss?.();
    }, [onDismiss]);

    if (isDismissed) return null;

    return (
        <ExtendedCard
            classNames={{
                base: twMerge(
                    'relative bg-white pt-0 pr-0 pb-0 pl-0',
                    getWrapperBorderClass(wrapperBorder),
                    getWidthClass(width, dismissible),
                    getOverflowVisibilityClass(allowOverflow, dismissible),
                    className,
                ),
                header: getHeaderPadding({ padding }),
                body: twMerge(
                    getBodyPadding({ dividers, padding, header }),
                    getStartBorder(startBorderColor),
                    allowOverflow && 'overflow-visible',
                ),
            }}
            shadow={shadow ? 'card' : 'none'}
            radius={radius}
        >
            {dismissible && (
                <div className="pointer-events-auto absolute -right-3 -top-3 z-20">
                    <DismissButton onPress={handleDismiss} />
                </div>
            )}
            {header && <CardHeader>{header}</CardHeader>}
            {dividers.afterHeader && header && <Divider />}
            <CardBody>{children}</CardBody>
        </ExtendedCard>
    );
}
